import { datadogRum } from '@datadog/browser-rum'

import { getFlagFromLocalStorageOrWindow } from '~/notes/utils/localStorageFlags'

import {
  flagHasVariant,
  getFlagVariantValue,
  UnleashClient,
  unleashClient as regardUnleashClient,
} from './unleash'

interface GetFlagValueOptions<K extends keyof Flags> {
  defaultValue?: Flags[K] | undefined
  getFlagValue?: (key: K) => Flags[K] | undefined
}

const defaultOptions = {
  getFlagValue: getFlagFromLocalStorageOrWindow,
  defaultValue: undefined,
} as const

export const getFlagValue = <K extends keyof Flags>(
  flagKey: K,
  options: GetFlagValueOptions<K> = {},
  unleashClient: UnleashClient = regardUnleashClient,
): Flags[K] | undefined => {
  const { getFlagValue: getFlagValueGetter, defaultValue } = {
    ...defaultOptions,
    ...options,
  }

  let resolvedValue = defaultValue
  const localValue = getFlagValueGetter(flagKey)
  if (localValue !== undefined) {
    resolvedValue = localValue
  } else if (unleashClient?.isEnabled(flagKey)) {
    const variant = unleashClient.getVariant(flagKey)
    const hasVariant = flagHasVariant(variant)
    if (hasVariant) {
      resolvedValue = getFlagVariantValue(flagKey, variant)
    }
  }

  datadogRum.addFeatureFlagEvaluation(flagKey, resolvedValue)
  return resolvedValue
}

export const createGetFlagValueGetter =
  <K extends keyof Flags>(
    flagKey: K,
    options?: GetFlagValueOptions<K>,
    unleashClient?: UnleashClient,
  ): (() => Flags[K] | undefined) =>
  () =>
    getFlagValue(flagKey, options, unleashClient)
